import { types } from 'mobx-state-tree'

import { getRootStore } from '#state/models/getRootStore'
import { ExplorerViewType } from '#state/types'

import { BaseModel } from '../BaseModel'

// This models state of the sidebar buttons, selected view, collapsed state
// etc. The reason it has relatively few props is largely because so much of
// the state of the explorer is persisted in UserPreferences (and thus this model
// accesses it through the root store helper)
export const ExplorerState = BaseModel.named('ExplorerState')
  .props({
    collectionsExpanded: true,
    dragging: false,
    collapsedSections: types.array(types.string),
    // if the user clicks the crosshairs to locate a folder, we store the
    // data here until we can scroll to or jiggle the requested items (they may not
    // actually be in the dom yet)
    crosshairsRequestFolderId: types.maybe(types.string),
    // these are selectors for dom nodes we've added css classes to. The useDocLocator
    // uses this to clean up the classes after a timeout
    jiggledSelectors: types.array(types.string),
    // when navigating from the snapshot history to a snapshot, store this
    // so we know where to return to
    lastScriptPath: types.maybe(types.string),
  })
  .views((self) => ({
    // Get the user's most recently selected explorer view IF it is currently valid
    // (if no preference or not valid, use 'browser' which is always valid)
    get selectedView(): ExplorerViewType {
      const preferredView =
        getRootStore(self).user.prefs.sidebarCurrentPane ?? 'browser'
      return this.enabledViews.includes(preferredView)
        ? preferredView
        : 'browser'
    },
    get enabledViews(): ExplorerViewType[] {
      // i suspect there's probably a less brittle way to
      // determine the current route type than any of these
      const isScriptRoute = getRootStore(self).location.getPathParam('scriptId')
      const isRundownRoute =
        getRootStore(self).location.getPathParam('rundownId')
      const hasPages = !getRootStore(self).user.prefs.pageless

      const isFolderRoute =
        getRootStore(self).location.pathname.includes('/folders/') ||
        getRootStore(self).location.pathname === '/'

      const isArchivedScriptRoute =
        getRootStore(self).location.pathname.includes('/wayback/')

      const result: ExplorerViewType[] = []
      if (
        isFolderRoute ||
        isRundownRoute ||
        isScriptRoute ||
        isArchivedScriptRoute
      ) {
        result.push('browser', 'search')
      }
      if (isRundownRoute || isScriptRoute) {
        result.push('outline')
      }
      if (isScriptRoute && hasPages) {
        result.push('pages')
      }

      return result
    },
    get explorerOpenWidth(): number | undefined {
      return getRootStore(self).user.prefs.sidebarOpenWidth
    },
    get isExplorerClosed(): boolean {
      return (
        !!getRootStore(self).user.prefs.sidebarClosed ||
        this.enabledViews.length === 0
      )
    },
    isSectionCollapsed(sectionId: string): boolean {
      return self.collapsedSections.includes(sectionId)
    },
  }))
  .actions((self) => ({
    expandFolderView() {
      this.setExplorerClosed(false)
      this.selectView('browser')
      self.collapsedSections.remove('folders')
      self.collapsedSections.remove('documents')
    },
    toggleCollections() {
      self.collectionsExpanded = !self.collectionsExpanded
    },
    setDragging(value: boolean) {
      self.dragging = value
    },
    selectView(view: ExplorerViewType) {
      if (self.enabledViews.includes(view)) {
        const isLeavingPagesView =
          self.selectedView === 'pages' && view !== 'pages'
        if (isLeavingPagesView) {
          const currentScript = getRootStore(self).currentScript
          if (currentScript?.titlePage.titlePageVisible) {
            currentScript.titlePage.setTitlePageVisible(false)
          }
        }

        getRootStore(self).user.updatePreferences({ sidebarCurrentPane: view })
      }
    },
    setExplorerClosed(value: boolean) {
      getRootStore(self).user.updatePreferences({
        sidebarClosed: value,
      })
    },
    setExplorerWidth(value: number) {
      getRootStore(self).user.updatePreferences({ sidebarOpenWidth: value })
    },
    setSectionCollapsed(sectionId: string, value: boolean) {
      const currentValue = self.collapsedSections.includes(sectionId)
      if (currentValue && !value) {
        self.collapsedSections.remove(sectionId)
      } else if (!currentValue && value) {
        self.collapsedSections.push(sectionId)
      }
    },
    toggleSectionCollapsed(sectionId: string) {
      if (self.collapsedSections.includes(sectionId)) {
        self.collapsedSections.remove(sectionId)
      } else {
        self.collapsedSections.push(sectionId)
      }
    },
    setCrosshairsFolderRequest(id: string) {
      self.crosshairsRequestFolderId = id
    },
    clearCrosshairsFolderRequest() {
      self.crosshairsRequestFolderId = undefined
    },
    setLastScriptPath(value: string) {
      self.lastScriptPath = value
    },
  }))
